import {
    BufferingIndicator,
    Controls,
    createPlayer,
    ErrorDialog,
    FullscreenButton,
    Gesture,
    Hotkey,
    Menu,
    PlayButton,
    Poster,
    SeekButton,
    SeekIndicator,
    Slider,
    StatusAnnouncer,
    StatusIndicator,
    Time,
    TimeSlider,
    Tooltip,
    useHotkey,
    usePlaybackRateOptions
} from '@videojs/react';
import {
    CheckIcon,
    ChevronIcon,
    FullscreenEnterIcon,
    FullscreenExitIcon,
    PauseIcon,
    PlayIcon,
    RestartIcon,
    SeekIcon,
    SpinnerIcon
} from '@videojs/react/icons';
import { videoFeatures } from '@videojs/react/video';
import { forwardRef, useEffect, type ComponentProps, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import '@videojs/react/video/skin.css';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { ROTATION_FULLSCREEN, releaseVideoRotation, rotateVideoTo } from '../utils/videoFullscreen';
import styles from './EventVideoSkin.module.css';

const SEEK_TIME = 5;
const TOP_STATUS_ACTIONS = ['toggleFullscreen'] as const;
const CENTER_STATUS_ACTIONS = ['togglePaused'] as const;

export const EVENT_VIDEO_FPS = 10;

// Frame centers, not boundaries: seeks land slightly off the requested time,
// so boundary targets can display the neighbouring frame.
export const frameCenterTime = (frameIndex: number) => (frameIndex + 0.5) / EVENT_VIDEO_FPS;

export const EventVideoPlayer = createPlayer({ features: videoFeatures });
// The library types useMedia() as a narrow Media facade, but it hands back the
// real element; type the boundary as such so consumers get the full
// HTMLMediaElement API (currentTime, paused, load, event listeners, ...).
export type EventVideoMedia = HTMLVideoElement | null;

type EventVideoSkinProps = {
    children: ReactNode,
    currentFrameIndex: number,
    frameCount?: number,
    poster: string
};

const Button = forwardRef<HTMLButtonElement, ComponentProps<'button'>>(function Button({ className, ...props }, ref) {
    return (
        <button
            {...props}
            ref={ref}
            type="button"
            data-interactive
            className={`media-button media-button--subtle media-button--icon ${className ?? ''}`}
        />
    );
});

// The player's full default rate list (0.2x through 2x in eight steps) is
// denser than useful for clips this short; offer just these.
const MENU_PLAYBACK_RATES = [0.2, 0.5, 1, 2];

// beta.26 dropped the PlaybackRateMenu compound; this recreates it from the
// generic Menu plus usePlaybackRateOptions. The skin renders the trigger's
// rate label through CSS (`.media-button--playback-rate::after` reads
// data-rate), so the trigger only supplies the attribute.
function PlaybackRateMenu(): ReactNode {
    const { t } = useTranslation('pages');
    const playbackRate = usePlaybackRateOptions();

    if (playbackRate?.state.availability !== 'available') {
        return null;
    }

    const { setValue, value, rate } = playbackRate;
    const options = playbackRate.options.filter((option) => MENU_PLAYBACK_RATES.includes(option.rate));

    return (
        <Menu.Root side="top" align="center">
            <Menu.Trigger
                className="media-button--playback-rate"
                aria-label={t('event.video.playback_rate')}
                data-rate={rate}
                render={<Button />}
            />
            {/* media-menu--settings is load-bearing: the skin scopes the
                menu's width/height (fed from --media-menu-* measurement vars)
                to that modifier, and the panel inside is absolutely
                positioned, so without it the popup collapses to its padding */}
            <Menu.Content className="media-surface media-popover media-menu media-menu--settings">
                <Menu.View className="media-menu__panel">
                    <Menu.RadioGroup className="media-menu__group" value={value} onValueChange={setValue} aria-label={t('event.video.playback_rate')}>
                        {options.map((option) => (
                            <Menu.RadioItem key={option.value} className="media-menu__item" value={option.value} disabled={option.disabled}>
                                <span>{option.label}</span>
                                <Menu.ItemIndicator checked={option.value === value} forceMount className="media-menu__indicator">
                                    <CheckIcon className="media-icon" />
                                </Menu.ItemIndicator>
                            </Menu.RadioItem>
                        ))}
                    </Menu.RadioGroup>
                </Menu.View>
            </Menu.Content>
        </Menu.Root>
    );
}

// On phones the fullscreen control rotates the app into the landscape layout
// instead of entering native element fullscreen (see utils/videoFullscreen).
// Same class and data-fullscreen contract as the library button, so the
// skin's icon-toggling CSS applies unchanged.
const RotateFullscreenButton = () => {
    const { t } = useTranslation('pages');
    // same query as the landscape rules in EventModal.module.css
    const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-height: 599px)');

    // never leave an orientation lock behind when the player goes away
    useEffect(() => releaseVideoRotation, []);

    const label = isMobileLandscape ? t('event.video.exit_fullscreen') : t('event.video.fullscreen');

    return (
        <Button
            className="media-button--fullscreen"
            title={label}
            aria-label={label}
            {...(isMobileLandscape ? { 'data-fullscreen': '' } : null)}
            onClick={() => rotateVideoTo(isMobileLandscape ? 'portrait' : 'landscape').catch(() => undefined)}
        >
            <FullscreenEnterIcon className="media-icon media-icon--fullscreen-enter" />
            <FullscreenExitIcon className="media-icon media-icon--fullscreen-exit" />
        </Button>
    );
};

// Inline SVGs (mirroring public/assets/event/*.svg) rather than IonIcon src
// fetches, so the icons render regardless of asset loading or shadow DOM
const PreviousFrameIcon = () => (
    <svg viewBox="0 0 100 100" fill="currentColor" className={`media-icon ${styles.frameIcon}`} aria-hidden="true">
        <path d="M76.2375 16.95c-5.3 0-9.7 4.4-9.7 9.7v46.7c0 5.3 4.4 9.7 9.7 9.7h2.7c5.3 0 9.7-4.4 9.7-9.7v-46.7c0-5.3-4.4-9.7-9.7-9.7z" />
        <path d="m14.4375 56.15 28.7 23.9c4.1 3.4 7.4 1.8 7.4-3.5v-53.2c0-5.3-3.3-6.9-7.4-3.5l-28.7 23.9c-4.1 3.4-4.1 9 0 12.4z" />
    </svg>
);

const NextFrameIcon = () => (
    <svg viewBox="0 0 100 100" fill="currentColor" className={`media-icon ${styles.frameIcon}`} aria-hidden="true">
        <path d="M21.0625 83h2.7c5.3 0 9.7-4.4 9.7-9.7V26.7c0-5.3-4.4-9.7-9.7-9.7h-2.7c-5.3 0-9.7 4.4-9.7 9.7v46.7c.1 5.3 4.4 9.6 9.7 9.6z" />
        <path d="M49.4625 23.4v53.2c0 5.3 3.3 6.9 7.4 3.5l28.7-23.9c4.1-3.4 4.1-9 0-12.4l-28.7-23.9c-4.1-3.4-7.4-1.8-7.4 3.5z" />
    </svg>
);

const FrameStepButton = ({
    disabled,
    icon,
    label,
    onActivate
}: {
    disabled: boolean,
    icon: ReactNode,
    label: string,
    onActivate: () => void
}) => (
    <Tooltip.Root side="top">
        <Tooltip.Trigger
            render={(
                <Button
                    title={label}
                    aria-label={label}
                    aria-disabled={disabled || undefined}
                    className={`media-button--seek ${styles.frameButton}`}
                    onClick={() => {
                        if (!disabled) {
                            onActivate();
                        }
                    }}
                >
                    {icon}
                </Button>
            )}
        />
        <Tooltip.Popup className="media-surface media-tooltip">{label}</Tooltip.Popup>
    </Tooltip.Root>
);

const EventVideoStepControls = ({
    currentFrameIndex,
    frameCount
}: Pick<EventVideoSkinProps, 'currentFrameIndex' | 'frameCount'>) => {
    const { t } = useTranslation('pages');
    const paused = EventVideoPlayer.usePlayer((state) => state.paused);
    const seek = EventVideoPlayer.usePlayer((state) => state.seek);

    const canStepBackward = currentFrameIndex > 0;
    const canStepForward = !frameCount || currentFrameIndex < frameCount - 1;

    // Absolute frame-center seeks so consecutive steps can't drift across
    // frame boundaries when the engine lands slightly off the requested time.
    const stepFrames = (delta: number) => seek(frameCenterTime(currentFrameIndex + delta));

    useHotkey({
        keys: 'ArrowLeft',
        disabled: !paused,
        onActivate: () => {
            if (canStepBackward) {
                stepFrames(-1);
            }
        }
    });

    useHotkey({
        keys: 'ArrowRight',
        disabled: !paused,
        onActivate: () => {
            if (canStepForward) {
                stepFrames(1);
            }
        }
    });

    return (
        <>
            <Hotkey keys="ArrowRight" action="seekStep" value={SEEK_TIME / 2} disabled={paused} />
            <Hotkey keys="ArrowLeft" action="seekStep" value={-(SEEK_TIME / 2)} disabled={paused} />
            {paused ? (
                <>
                    <FrameStepButton
                        disabled={!canStepBackward}
                        icon={<PreviousFrameIcon />}
                        label={t('event.video.previous_frame')}
                        onActivate={() => stepFrames(-1)}
                    />
                    <FrameStepButton
                        disabled={!canStepForward}
                        icon={<NextFrameIcon />}
                        label={t('event.video.next_frame')}
                        onActivate={() => stepFrames(1)}
                    />
                </>
            ) : (
                <>
                    <Tooltip.Root side="top">
                        <Tooltip.Trigger
                            render={(
                                <SeekButton seconds={-SEEK_TIME} className="media-button--seek" render={<Button />}>
                                    <span className="media-icon__container">
                                        <SeekIcon className="media-icon media-icon--seek media-icon--flipped" />
                                        <span className="media-icon__label">{SEEK_TIME}</span>
                                    </span>
                                </SeekButton>
                            )}
                        />
                        <Tooltip.Popup className="media-surface media-tooltip" />
                    </Tooltip.Root>
                    <Tooltip.Root side="top">
                        <Tooltip.Trigger
                            render={(
                                <SeekButton seconds={SEEK_TIME} className="media-button--seek" render={<Button />}>
                                    <span className="media-icon__container">
                                        <SeekIcon className="media-icon media-icon--seek" />
                                        <span className="media-icon__label">{SEEK_TIME}</span>
                                    </span>
                                </SeekButton>
                            )}
                        />
                        <Tooltip.Popup className="media-surface media-tooltip" />
                    </Tooltip.Root>
                </>
            )}
        </>
    );
};

export function EventVideoSkin({
    children,
    currentFrameIndex,
    frameCount,
    poster
}: EventVideoSkinProps): ReactNode {
    const { t } = useTranslation('pages');
    const { t: tCommon } = useTranslation('common');
    const paused = EventVideoPlayer.usePlayer((state) => state.paused);

    return (
        <EventVideoPlayer.Container className={`media-default-skin media-default-skin--video ${styles.root}`}>
            {children}

            <Poster src={poster} alt="" />

            <BufferingIndicator
                render={(props) => (
                    <div {...props} className="media-buffering-indicator">
                        <SpinnerIcon className="media-icon" />
                    </div>
                )}
            />

            <ErrorDialog.Root>
                <ErrorDialog.Popup className="media-error">
                    <div className="media-error__dialog media-surface">
                        <div className="media-error__content">
                            <ErrorDialog.Title className="media-error__title">{t('event.video.error_title')}</ErrorDialog.Title>
                            <ErrorDialog.Description className="media-error__description" />
                        </div>
                        <div className="media-error__actions">
                            <ErrorDialog.Close className="media-button media-button--primary">{tCommon('buttons.ok')}</ErrorDialog.Close>
                        </div>
                    </div>
                </ErrorDialog.Popup>
            </ErrorDialog.Root>

            {/* data-interactive: pointer-ups that slip off a button onto the bar (or fall
                through a disabled button) must not feed the tap/double-tap gesture recognizer */}
            {/* root + primary structure per the beta.26 skin.css contract: on wide
                players the root is the positioned bar and primary dissolves; on
                narrow players primary is the positioned surface. We render no
                secondary bar (upstream puts cast/pip/fullscreen top-right). */}
            <Controls.Root data-interactive className={`media-surface media-controls media-controls--root ${styles.controls}`}>
                <Tooltip.Provider>
                    <div className="media-surface media-controls media-controls--primary">
                        <div className="media-button-group">
                            <Tooltip.Root side="top">
                                <Tooltip.Trigger
                                    render={(
                                        <PlayButton className="media-button--play" render={<Button />}>
                                            <RestartIcon className="media-icon media-icon--restart" />
                                            <PlayIcon className="media-icon media-icon--play" />
                                            <PauseIcon className="media-icon media-icon--pause" />
                                        </PlayButton>
                                    )}
                                />
                                <Tooltip.Popup className="media-surface media-tooltip">
                                    <Tooltip.Label />
                                    <Tooltip.Shortcut className="media-tooltip__kbd" />
                                </Tooltip.Popup>
                            </Tooltip.Root>

                            <EventVideoStepControls
                                currentFrameIndex={currentFrameIndex}
                                frameCount={frameCount}
                            />
                        </div>

                        <div className="media-time-controls">
                            <Time.Value type="current" className="media-time" />
                            <TimeSlider.Root className="media-slider">
                                <TimeSlider.Track className="media-slider__track">
                                    <TimeSlider.Fill className="media-slider__fill" />
                                    <TimeSlider.Buffer className="media-slider__buffer" />
                                </TimeSlider.Track>
                                <TimeSlider.Thumb className="media-slider__thumb" />

                                {/* no thumbnail box: these clips carry no storyboard track.
                                    media-surface on the value restores the frosted scrub-time
                                    bubble beta.26 reduced to bare text (illegible over bright
                                    frames); the pill sizing lives in the module css */}
                                <Slider.Preview className="media-slider__preview">
                                    <TimeSlider.Value type="pointer" className="media-surface media-time media-slider__value" />
                                </Slider.Preview>
                            </TimeSlider.Root>
                            <Time.Value type="duration" className="media-time" />
                        </div>

                        <div className="media-button-group">
                            <PlaybackRateMenu />

                            {ROTATION_FULLSCREEN ? (
                                <RotateFullscreenButton />
                            ) : (
                                <Tooltip.Root side="top">
                                    <Tooltip.Trigger
                                        render={(
                                            <FullscreenButton className="media-button--fullscreen" render={<Button />}>
                                                <FullscreenEnterIcon className="media-icon media-icon--fullscreen-enter" />
                                                <FullscreenExitIcon className="media-icon media-icon--fullscreen-exit" />
                                            </FullscreenButton>
                                        )}
                                    />
                                    <Tooltip.Popup className="media-surface media-tooltip">
                                        <Tooltip.Label />
                                        <Tooltip.Shortcut className="media-tooltip__kbd" />
                                    </Tooltip.Popup>
                                </Tooltip.Root>
                            )}
                        </div>
                    </div>
                </Tooltip.Provider>
            </Controls.Root>

            <div className={`media-overlay ${styles.overlay}`} />

            <Hotkey keys="Space" action="togglePaused" />
            <Hotkey keys="k" action="togglePaused" />
            {/* native element fullscreen is desktop/tablet-only; phones rotate
                into the landscape layout via the button instead */}
            {!ROTATION_FULLSCREEN && <Hotkey keys="f" action="toggleFullscreen" />}
            <Hotkey keys="l" action="seekStep" value={SEEK_TIME} />
            <Hotkey keys="j" action="seekStep" value={-SEEK_TIME} />
            <Hotkey keys="0-9" action="seekToPercent" />
            <Hotkey keys="Home" action="seekToPercent" value={0} />
            <Hotkey keys="End" action="seekToPercent" value={100} />
            <Hotkey keys=">" action="speedUp" />
            <Hotkey keys="<" action="speedDown" />

            <Gesture type="tap" action="togglePaused" pointer="mouse" region="center" />
            <Gesture type="tap" action="toggleControls" pointer="touch" />
            {/* disabled while paused: rapid frame-step taps that land just off the
                controls bar must not register as double-tap skips */}
            <Gesture type="doubletap" action="seekStep" value={-SEEK_TIME} region="left" disabled={paused} />
            <Gesture type="doubletap" action="toggleFullscreen" region="center" disabled={ROTATION_FULLSCREEN} />
            <Gesture type="doubletap" action="seekStep" value={SEEK_TIME} region="right" disabled={paused} />

            {/* beta.26 no longer hides the announcer itself; without the class
                the aria-live status text renders visibly inside the player */}
            <StatusAnnouncer className="media-sr-only" />
            <div className="media-input-feedback">
                <StatusIndicator.Root actions={TOP_STATUS_ACTIONS} className="media-surface media-input-feedback-island media-input-feedback-island--status">
                    <div className="media-input-feedback-island__content">
                        <FullscreenEnterIcon className="media-icon media-icon--fullscreen-enter" />
                        <FullscreenExitIcon className="media-icon media-icon--fullscreen-exit" />
                        <StatusIndicator.Value className="media-input-feedback-island__value" />
                    </div>
                </StatusIndicator.Root>

                <SeekIndicator.Root className="media-input-feedback-bubble">
                    <ChevronIcon className="media-icon media-icon--seek" />
                    <SeekIndicator.Value className="media-time" />
                </SeekIndicator.Root>

                <StatusIndicator.Root actions={CENTER_STATUS_ACTIONS} className="media-input-feedback-bubble">
                    <PlayIcon className="media-icon media-icon--play" />
                    <PauseIcon className="media-icon media-icon--pause" />
                </StatusIndicator.Root>
            </div>
        </EventVideoPlayer.Container>
    );
}
